import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
} from 'angular-ts-decorators';
import {
  faBuilding,
  faExchange,
  faLifeRing,
  faSignOut,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { Navigate } from '@core/app/scripts/services/navigate';
import { NotificationManager } from '@shared/app/scripts/services/notification-manager';
import { CurrentSession } from '@shared/app/scripts/services/current-session';
import { SessionManager } from '@shared/app/scripts/services/session-manager';
import { find } from 'lodash';
import { ConnectionStatus } from '@core/app/scripts/services/connection-status';
import { WindowStreams } from '@core/app/scripts/services/window-streams';

const { startWith } = require('rxjs/operators');
/**
 * Renders the top bar/nav bar. Should be run
 * in index.html
 */
@Component({
  selector: 'swHeader',
  transclude: {
    links: '?menuLinks',
  },
  template: require('./sw-header.html').default,
})
export class SwHeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  menuLinks: any;
  menuHover: boolean;
  isMobileMenuShown: boolean;
  isLoginPage: boolean;
  isOffline: boolean;

  faExchange = faExchange;
  faBuilding = faBuilding;
  faUser = faUser;
  faLifeRing = faLifeRing;
  faSignOut = faSignOut;
  domainClass: string;
  faviconUrl: string;

  mobileScreenWidth = 599;
  navbarHeight = 62;
  navbarHeightMobile = 48;

  // Hack! To be replaced by custom operator rx operator
  wrapInSafeApply = require('@global/wrap-in-safe-apply')(this.$rootScope);

  private iFrameResizeObserver: ResizeObserver;
  private iFrameHeightUpdateListener: () => void;

  constructor(
    private $anchorScroll: ng.IAnchorScrollService,
    private $element: ng.IRootElementService,
    private $location: ng.ILocationService,
    private $document: ng.IDocumentService,
    private $rootScope: ng.IRootScopeService,
    private $route: ng.route.IRouteService,
    private $translate: ng.translate.ITranslateService,
    private $window: ng.IWindowService,
    private connectionStatus: ConnectionStatus,
    private currentSession: CurrentSession,
    private navigate: Navigate,
    private notificationManager: NotificationManager,
    private sessionManager: SessionManager,
    private windowStreams: WindowStreams,
  ) {
    'ngInject';

    this.menuHover = false;
    this.isMobileMenuShown = false;
    this.isLoginPage = this.$location.path() === '/login';
  }

  private sendiFrameHeightToParent() {
    const body = this.$document[0].body;
    const hasHorizontalScrollbar = body.scrollWidth > body.clientWidth;
    // Height extending is to not let the last row of the tables being hidden behind the horizontal scrollbar
    const heightExtending = hasHorizontalScrollbar ? 15 : 0;
    const height = body.scrollHeight + heightExtending;
    // Send message to parent with the height
    this.$window.parent.postMessage({ type: 'iframe-height', height }, '*');
  }

  private setupiFrameHeightObserver() {
    // Send initial height
    this.sendiFrameHeightToParent();

    // Use ResizeObserver to keep the height up-to-date
    this.iFrameResizeObserver = new ResizeObserver(
      this.sendiFrameHeightToParent.bind(this),
    );
    this.iFrameResizeObserver.observe(this.$document[0].body);
  }

  ngOnInit() {
    this.windowStreams.resize$.pipe(startWith(undefined)).subscribe(() => {
      this.$anchorScroll.yOffset =
        this.$window.innerWidth > this.mobileScreenWidth
          ? this.navbarHeight
          : this.navbarHeightMobile;
    });

    this.isOffline = false;

    this.connectionStatus.isOnline$.subscribe(
      this.wrapInSafeApply((value) => {
        this.isOffline = !value;
      }),
    );

    const host = this.$window.location.host;
    if (host.includes('qliqpoint')) {
      this.domainClass = 'qliqpoint';
      this.faviconUrl = `/favicon-${this.domainClass}.ico?v=${Date.now()}`;
    } else {
      this.domainClass = 'softwearconnect';
      this.faviconUrl = `/favicon.ico?v=${Date.now()}`;
    }

    // Remove existing favicon links
    const existingFavicons = this.$document[0].querySelectorAll(
      'link[rel="icon"], link[rel="shortcut icon"]',
    );
    existingFavicons.forEach((link) => link.parentNode.removeChild(link));

    // Create and append the new favicon link
    const newFavicon = this.$document[0].createElement('link');
    newFavicon.rel = 'icon';
    newFavicon.href = this.faviconUrl;
    newFavicon.type = 'image/x-icon';

    const newShortcutIcon = this.$document[0].createElement('link');
    newShortcutIcon.rel = 'shortcut icon';
    newShortcutIcon.href = this.faviconUrl;
    newShortcutIcon.type = 'image/x-icon';

    this.$document[0].head.appendChild(newFavicon);
    this.$document[0].head.appendChild(newShortcutIcon);

    // Set up iframe height observer if we're in an iframe
    if (this.$window.self !== this.$window.top) {
      this.iFrameHeightUpdateListener =
        this.setupiFrameHeightObserver.bind(this);

      // Check if DOMContentLoaded has already fired
      if (this.$document[0].readyState === 'loading') {
        this.$window.addEventListener(
          'DOMContentLoaded',
          this.iFrameHeightUpdateListener,
        );
      } else {
        this.setupiFrameHeightObserver();
      }
    }
  }

  ngOnDestroy() {
    // Remove DOMContentLoaded event listener
    if (this.iFrameHeightUpdateListener) {
      this.$window.removeEventListener(
        'DOMContentLoaded',
        this.iFrameHeightUpdateListener,
      );
    }

    // Disconnect ResizeObserver
    if (this.iFrameResizeObserver) {
      this.iFrameResizeObserver.disconnect();
    }

    this.menuLinks.on('click', () => this.toggleMobileMenu());
  }

  ngAfterViewInit() {
    this.menuLinks = this.$element.find('menu-links').find('a');
    this.menuLinks.on('click', () => this.toggleMobileMenu());

    this.$rootScope.$on('$locationChangeStart', () => {
      this.closeMobileMenu();
      this.closeMenu();
    });

    // TODO cover with test
    this.$rootScope.$on(
      '$locationChangeSuccess',
      () => (this.isLoginPage = this.$location.path() === '/login'),
    );
  }

  // TODO: This – and also the click-outside stuff
  // –  should be put into a dedicated directive:
  // dialog - displays a dialog window at a specific
  // location
  toggleMenu() {
    this.menuHover = !this.menuHover;
  }

  closeMenu() {
    this.menuHover = false;
  }

  closeMobileMenu() {
    this.isMobileMenuShown = false;
  }

  toggleMobileMenu() {
    this.isMobileMenuShown = !this.isMobileMenuShown;
  }

  logout() {
    return this.sessionManager
      .logout()
      .then((willReload) => {
        if (!willReload) {
          this.$route.reload();
        }
      })
      .catch((error) => {
        this.notificationManager.sendError(
          'Failed logout with: ' + error,
          'logoutFailed',
          undefined,
          new Error(error),
        );
      });
  }

  switchAccount() {
    this.navigate.to('www', '/choose-tenant', true);
  }

  hasMultipleTenants() {
    return (
      angular.isArray(this.currentSession.availableTenants) &&
      this.currentSession.availableTenants.length > 1
    );
  }

  getTenantName() {
    let currentTenant;
    if (this.currentSession.tenantId) {
      currentTenant = find(this.currentSession.availableTenants, {
        id: this.currentSession.tenantId,
      });
      return angular.isObject(currentTenant)
        ? currentTenant.name
        : this.$translate.instant('core.unknown');
    } else {
      return this.$translate.instant('core.unknown');
    }
  }
}
